import { useCallback, useEffect, useRef, useState } from 'react';

import useStaticCallback from 'hooks/useStaticCallback';
import { Size } from 'types';
import { WAVEFORM_DEFAULT_COLOR } from 'utils/constants';
import { renderBarWaveform } from 'utils/waveform-renderer';
import { usePlayback } from './context/PlaybackContext';
import { usePlayerSize } from './context/PlayerSizeContext';

export interface WaveformPlayer {
  on: (event: 'play' | 'pause', fn: () => void) => void;
  off: (event: 'play' | 'pause', fn: () => void) => void;
  readonly isReady: boolean;
  readonly paused: boolean;
  readonly waveformData: Uint8Array;
}

export type GetWaveformCanvasSize = (playerDimensions: Size) => Size;

export interface UseWaveformProps {
  color?: string;
  // canvas size needs to be set explicitly on the canvas element so as to not
  // distort the image as it scales.   this function can be used to customize the
  // height and width of the waveform relative to the player
  getCanvasSize?: GetWaveformCanvasSize;
  // sample is rendered as the placeholder waveform before the user interacts
  // with the player
  sampleFrame?: number[];
}

export default function useWaveform({
  getCanvasSize = (size) => size,
  color = WAVEFORM_DEFAULT_COLOR,
  sampleFrame,
}: UseWaveformProps) {
  const playerSize = usePlayerSize();
  const { player, ready } = usePlayback();
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const [canvasSize, setCanvasSize] = useState<Size>({ height: 0, width: 0 });

  const [context, setContext] = useState<CanvasRenderingContext2D>();

  const rafIdRef = useRef<number>();
  const sampleFrameRef = useRef(sampleFrame);

  const staticGetCanvasSize = useStaticCallback(getCanvasSize);

  // this effect needs to run when player dimensions or canvas ref change.  all other
  // deps should be static to avoid unnecessary updates
  useEffect(() => {
    if (playerSize?.height && playerSize?.width && canvas) {
      setCanvasSize(staticGetCanvasSize(playerSize));
      setContext(canvas.getContext('2d') as CanvasRenderingContext2D);
    }
  }, [canvas, playerSize, staticGetCanvasSize]);

  const draw = useCallback(() => {
    // if there's no context, can't render anything so bail
    if (!context) return;

    //  if the player is ready, sample can come from the player.  if the player is
    //  paused, the Analyser node typically returns an array of zeros, but the player
    //  caches the last sample and will return that when paused
    const sample = ready
      ? player?.waveformData
      : sampleFrameRef.current
      ? new Uint8Array(sampleFrameRef.current)
      : undefined;

    if (!sample) {
      return;
    }

    renderBarWaveform(context, sample, { color });
  }, [color, context, ready, player?.waveformData]);

  const drawLoop = useCallback(() => {
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        draw();
        rafIdRef.current = undefined;
        drawLoop();
      });
    }
  }, [draw]);

  const cancelDrawLoop = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = undefined;
    }
  }, []);

  useEffect(
    () => {
      if (player) {
        player.on('play', drawLoop);
        player.on('pause', cancelDrawLoop);
      }

      // - if player is not ready, draw() will use the sample frame
      // - if player is ready and it's paused, we should still call draw()
      //   in case the canvasSize has changed.  changing the canvas size will
      //   automatically clear it, requireing a repaint
      if (player) {
        if (!ready || player.paused) {
          draw();
        } else if (!player.paused) {
          drawLoop();
        }
      }
      return () => {
        cancelDrawLoop();

        player?.off('play', drawLoop);
        player?.off('pause', cancelDrawLoop);
      };
    },
    // canvasSize is not explicitly used in this hook but needs to be here to trigger
    // a repaint when the canvas dimensions change
    [cancelDrawLoop, draw, drawLoop, player, ready, canvasSize],
  );

  return { canvasSize, setCanvas };
}
